// The app page shell (DES1 `app-parcels*`, `app-error-*`, `app-deleted`): header, tabs, the
// failure banner and the deleted state. Tab content comes from the slots and surfaces elsewhere.
//
// Namespace `app-page`; owner: T2-UI1 (ui/workspace/apps/page). `{app}` app name, `{n}` version,
// `{people}` count, `{time}` a formatted time, `{who}` a person, `{date}` a formatted date,
// `{days}` retention. Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  back: 'Library',
  tabsLabel: 'App sections',
  tabApp: 'App',
  tabData: 'Data',
  tabHistory: 'History',
  tabVersions: 'Versions',
  owner: 'Owner {who}',
  saved: 'Saved · v{n}',
  unshare: 'Unshare',
  noLongerShared: 'This App is no longer shared. Ask {who} to share it again.',
  moreActions: 'More actions',
  duplicate: 'Duplicate…',
  reset: 'Clear all entries…',
  openChat: 'Open original chat',
  changeInChat: 'Change with Chat',
  changeInChatUnavailable: 'The chat this App was made in isn’t available.',
  failTitle: 'Version {n} stopped loading for {people} people at {time}.',
  failGoBack: 'Go back to v{n} for everyone',
  failFix: 'Fix in chat',
  failDetails: 'Details',
  failEmpty: 'This version didn’t load. People see the same message and a way to tell you.',
  failUser: 'This app didn’t load. Tell {who} so they can fix it.',
  failTell: 'Tell {who}',
  deletedTitle: 'This app was deleted',
  deletedBody:
    '{who} deleted {app} on {date}. Its entries are kept for {days} days in case it’s restored.',
  toLibrary: 'Go to Library',
  askPerson: 'Ask {who}',
  loading: 'Opening {app}…',
  appUnavailable: 'This app can’t be opened right now.',
  portionUnavailable: 'This part isn’t available right now.',
  appNoBuiltVersion: 'This app doesn’t have a built version to open yet.',
};

export type AppPageCopyKey = keyof typeof en;
type Copy = Record<AppPageCopyKey, string>;

const sv: Copy = {
  back: 'Bibliotek',
  tabsLabel: 'Appens delar',
  tabApp: 'App',
  tabData: 'Data',
  tabHistory: 'Historik',
  tabVersions: 'Versioner',
  owner: 'Ägare {who}',
  saved: 'Sparad · v{n}',
  unshare: 'Sluta dela',
  noLongerShared: 'Den här appen delas inte längre. Be {who} att dela den igen.',
  moreActions: 'Fler åtgärder',
  duplicate: 'Duplicera…',
  reset: 'Rensa alla poster…',
  openChat: 'Öppna ursprunglig chatt',
  changeInChat: 'Ändra i chatten',
  changeInChatUnavailable: 'Chatten som appen gjordes i är inte tillgänglig.',
  failTitle: 'Version {n} slutade laddas för {people} personer kl. {time}.',
  failGoBack: 'Gå tillbaka till v{n} för alla',
  failFix: 'Åtgärda i chatten',
  failDetails: 'Detaljer',
  failEmpty:
    'Den här versionen laddades inte. Folk ser samma meddelande och ett sätt att höra av sig.',
  failUser: 'Appen laddades inte. Säg till {who} så att det kan åtgärdas.',
  failTell: 'Säg till {who}',
  deletedTitle: 'Appen har tagits bort',
  deletedBody:
    '{who} tog bort {app} den {date}. Posterna sparas i {days} dagar om den skulle återställas.',
  toLibrary: 'Till biblioteket',
  askPerson: 'Fråga {who}',
  loading: 'Öppnar {app}…',
  appUnavailable: 'Appen kan inte öppnas just nu.',
  portionUnavailable: 'Den här delen är inte tillgänglig just nu.',
  appNoBuiltVersion: 'Den här appen har ingen byggd version att öppna än.',
};

// Full translations (DES2); keys and placeholders identical to `en`.
const translated: Record<string, Copy> = {
  bg: {
    saved: 'Запазено · v{n}',
    unshare: 'Спиране на споделянето',
    noLongerShared: 'Това приложение вече не се споделя. Помолете {who} да го сподели отново.',
    back: 'Библиотека',
    tabsLabel: 'Раздели на приложението',
    tabApp: 'Приложение',
    tabData: 'Данни',
    tabHistory: 'История',
    tabVersions: 'Версии',
    owner: 'Собственик: {who}',
    moreActions: 'Още действия',
    duplicate: 'Дублиране…',
    reset: 'Изчистване на всички записи…',
    openChat: 'Отваряне на оригиналния чат',
    changeInChat: 'Промяна в чата',
    changeInChatUnavailable: 'Чатът, в който е създадено това приложение, не е достъпен.',
    failTitle: 'Версия {n} спря да се зарежда за {people} души в {time}.',
    failGoBack: 'Връщане към v{n} за всички',
    failFix: 'Поправяне в чата',
    failDetails: 'Подробности',
    failEmpty: 'Тази версия не се зареди. Хората виждат същото съобщение и начин да Ви уведомят.',
    failUser: 'Това приложение не се зареди. Уведомете {who}, за да го поправи.',
    failTell: 'Уведомете {who}',
    deletedTitle: 'Това приложение е изтрито',
    deletedBody:
      '{who} изтри {app} на {date}. Записите му се пазят {days} дни, в случай че бъде възстановено.',
    toLibrary: 'Към библиотеката',
    askPerson: 'Попитайте {who}',
    loading: 'Отваряне на {app}…',
    appUnavailable: 'Това приложение не може да бъде отворено в момента.',
    portionUnavailable: 'Тази част не е налична в момента.',
    appNoBuiltVersion: 'Това приложение все още няма създадена версия, която да се отвори.',
  },
  cs: {
    saved: 'Uloženo · v{n}',
    unshare: 'Přestat sdílet',
    noLongerShared: 'Tato aplikace již není sdílena. Požádejte {who} o nové sdílení.',
    back: 'Knihovna',
    tabsLabel: 'Části aplikace',
    tabApp: 'Aplikace',
    tabData: 'Data',
    tabHistory: 'Historie',
    tabVersions: 'Verze',
    owner: 'Vlastník: {who}',
    moreActions: 'Další akce',
    duplicate: 'Duplikovat…',
    reset: 'Vymazat všechny záznamy…',
    openChat: 'Otevřít původní chat',
    changeInChat: 'Změnit v chatu',
    changeInChatUnavailable: 'Chat, ve kterém tato aplikace vznikla, není k dispozici.',
    failTitle: 'Verze {n} se přestala načítat v {time}. Počet dotčených lidí: {people}.',
    failGoBack: 'Vrátit se k v{n} pro všechny',
    failFix: 'Opravit v chatu',
    failDetails: 'Podrobnosti',
    failEmpty: 'Tato verze se nenačetla. Lidé vidí stejnou zprávu a možnost dát vám vědět.',
    failUser: 'Tato aplikace se nenačetla. Dejte vědět osobě {who}, aby ji mohla opravit.',
    failTell: 'Dát vědět osobě {who}',
    deletedTitle: 'Tato aplikace byla smazána',
    deletedBody:
      'Aplikaci {app} smazala dne {date} osoba {who}. Záznamy se pro případ obnovení uchovávají po omezenou dobu (počet dní: {days}).',
    toLibrary: 'Přejít do Knihovny',
    askPerson: 'Zeptat se osoby {who}',
    loading: 'Otevírá se {app}…',
    appUnavailable: 'Tuto aplikaci teď nelze otevřít.',
    portionUnavailable: 'Tato část teď není k dispozici.',
    appNoBuiltVersion: 'Tato aplikace zatím nemá vytvořenou verzi, kterou by šlo otevřít.',
  },
  da: {
    back: 'Bibliotek',
    tabsLabel: 'Appens sektioner',
    tabApp: 'App',
    tabData: 'Data',
    tabHistory: 'Historik',
    tabVersions: 'Versioner',
    owner: 'Ejer: {who}',
    saved: 'Gemt · v{n}',
    unshare: 'Stop deling',
    noLongerShared: 'Denne app deles ikke længere. Bed {who} om at dele den igen.',
    moreActions: 'Flere handlinger',
    duplicate: 'Dupliker…',
    reset: 'Ryd alle poster…',
    openChat: 'Åbn oprindelig chat',
    changeInChat: 'Ændr i chatten',
    changeInChatUnavailable: 'Chatten, som appen blev lavet i, er ikke tilgængelig.',
    failTitle: 'Version {n} holdt op med at indlæse for {people} personer kl. {time}.',
    failGoBack: 'Gå tilbage til v{n} for alle',
    failFix: 'Ret i chatten',
    failDetails: 'Detaljer',
    failEmpty:
      'Denne version blev ikke indlæst. Folk ser den samme besked og en måde at give dig besked på.',
    failUser: 'Denne app blev ikke indlæst. Giv {who} besked, så de kan rette den.',
    failTell: 'Giv {who} besked',
    deletedTitle: 'Denne app er slettet',
    deletedBody:
      '{who} slettede {app} den {date}. Posterne gemmes i {days} dage, hvis den skulle blive gendannet.',
    toLibrary: 'Gå til biblioteket',
    askPerson: 'Spørg {who}',
    loading: 'Åbner {app}…',
    appUnavailable: 'Appen kan ikke åbnes lige nu.',
    portionUnavailable: 'Denne del er ikke tilgængelig lige nu.',
    appNoBuiltVersion: 'Denne app har endnu ikke en bygget version, der kan åbnes.',
  },
  de: {
    saved: 'Gespeichert · v{n}',
    unshare: 'Nicht mehr teilen',
    noLongerShared: 'Diese App wird nicht mehr geteilt. Bitte {who}, sie erneut zu teilen.',
    back: 'Bibliothek',
    tabsLabel: 'App-Bereiche',
    tabApp: 'App',
    tabData: 'Daten',
    tabHistory: 'Verlauf',
    tabVersions: 'Versionen',
    owner: 'Inhaber: {who}',
    moreActions: 'Weitere Aktionen',
    duplicate: 'Duplizieren…',
    reset: 'Alle Einträge leeren…',
    openChat: 'Ursprünglichen Chat öffnen',
    changeInChat: 'Im Chat ändern',
    changeInChatUnavailable: 'Der Chat, in dem diese App entstanden ist, ist nicht verfügbar.',
    failTitle: 'Version {n} lädt seit {time} für {people} Personen nicht mehr.',
    failGoBack: 'Für alle zu v{n} zurückkehren',
    failFix: 'Im Chat beheben',
    failDetails: 'Einzelheiten',
    failEmpty:
      'Diese Version wurde nicht geladen. Alle sehen dieselbe Meldung und eine Möglichkeit, Ihnen Bescheid zu geben.',
    failUser:
      'Diese App wurde nicht geladen. Geben Sie {who} Bescheid, damit das behoben werden kann.',
    failTell: '{who} Bescheid geben',
    deletedTitle: 'Diese App wurde gelöscht',
    deletedBody:
      '{who} hat {app} am {date} gelöscht. Die Einträge werden {days} Tage aufbewahrt, falls die App wiederhergestellt wird.',
    toLibrary: 'Zur Bibliothek',
    askPerson: '{who} fragen',
    loading: '{app} wird geöffnet…',
    appUnavailable: 'Diese App kann gerade nicht geöffnet werden.',
    portionUnavailable: 'Dieser Teil ist gerade nicht verfügbar.',
    appNoBuiltVersion: 'Diese App hat noch keine erstellte Version, die geöffnet werden kann.',
  },
  el: {
    saved: 'Αποθηκεύτηκε · v{n}',
    unshare: 'Διακοπή κοινής χρήσης',
    noLongerShared:
      'Αυτή η εφαρμογή δεν κοινοποιείται πλέον. Ζητήστε από {who} να την κοινοποιήσει ξανά.',
    back: 'Βιβλιοθήκη',
    tabsLabel: 'Ενότητες εφαρμογής',
    tabApp: 'Εφαρμογή',
    tabData: 'Δεδομένα',
    tabHistory: 'Ιστορικό',
    tabVersions: 'Εκδόσεις',
    owner: 'Κάτοχος: {who}',
    moreActions: 'Περισσότερες ενέργειες',
    duplicate: 'Δημιουργία αντιγράφου…',
    reset: 'Εκκαθάριση όλων των εγγραφών…',
    openChat: 'Άνοιγμα αρχικής συνομιλίας',
    changeInChat: 'Αλλαγή στη συνομιλία',
    changeInChatUnavailable: 'Η συνομιλία όπου δημιουργήθηκε αυτή η εφαρμογή δεν είναι διαθέσιμη.',
    failTitle: 'Η έκδοση {n} σταμάτησε να φορτώνει για {people} άτομα στις {time}.',
    failGoBack: 'Επιστροφή στην v{n} για όλους',
    failFix: 'Διόρθωση στη συνομιλία',
    failDetails: 'Λεπτομέρειες',
    failEmpty:
      'Αυτή η έκδοση δεν φορτώθηκε. Οι χρήστες βλέπουν το ίδιο μήνυμα και έναν τρόπο να σας ενημερώσουν.',
    failUser: 'Αυτή η εφαρμογή δεν φορτώθηκε. Ενημερώστε {who}, ώστε να τη διορθώσει.',
    failTell: 'Ενημερώστε {who}',
    deletedTitle: 'Αυτή η εφαρμογή διαγράφηκε',
    deletedBody:
      '{who} διέγραψε την εφαρμογή {app} στις {date}. Οι εγγραφές της διατηρούνται για {days} ημέρες σε περίπτωση επαναφοράς.',
    toLibrary: 'Μετάβαση στη Βιβλιοθήκη',
    askPerson: 'Ρωτήστε {who}',
    loading: 'Άνοιγμα της εφαρμογής {app}…',
    appUnavailable: 'Αυτή η εφαρμογή δεν μπορεί να ανοίξει αυτή τη στιγμή.',
    portionUnavailable: 'Αυτό το τμήμα δεν είναι διαθέσιμο αυτή τη στιγμή.',
    appNoBuiltVersion: 'Αυτή η εφαρμογή δεν έχει ακόμη δημιουργημένη έκδοση για άνοιγμα.',
  },
  es: {
    saved: 'Guardado · v{n}',
    unshare: 'Dejar de compartir',
    noLongerShared: 'Esta App ya no se comparte. Pide a {who} que la comparta de nuevo.',
    back: 'Biblioteca',
    tabsLabel: 'Secciones de la app',
    tabApp: 'App',
    tabData: 'Datos',
    tabHistory: 'Historial',
    tabVersions: 'Versiones',
    owner: 'Propietario: {who}',
    moreActions: 'Más acciones',
    duplicate: 'Duplicar…',
    reset: 'Borrar todas las entradas…',
    openChat: 'Abrir el chat original',
    changeInChat: 'Cambiar en el chat',
    changeInChatUnavailable: 'El chat en el que se creó esta app no está disponible.',
    failTitle: 'La versión {n} dejó de cargarse para {people} personas a las {time}.',
    failGoBack: 'Volver a v{n} para todos',
    failFix: 'Corregir en el chat',
    failDetails: 'Detalles',
    failEmpty:
      'Esta versión no se cargó. Las personas ven el mismo mensaje y una forma de avisarte.',
    failUser: 'Esta app no se cargó. Avisa a {who} para que pueda corregirla.',
    failTell: 'Avisar a {who}',
    deletedTitle: 'Esta app se eliminó',
    deletedBody:
      '{who} eliminó {app} el {date}. Sus entradas se guardan durante {days} días por si se restaura.',
    toLibrary: 'Ir a la biblioteca',
    askPerson: 'Preguntar a {who}',
    loading: 'Abriendo {app}…',
    appUnavailable: 'Esta app no se puede abrir ahora mismo.',
    portionUnavailable: 'Esta parte no está disponible ahora mismo.',
    appNoBuiltVersion: 'Esta app aún no tiene una versión creada para abrir.',
  },
  et: {
    saved: 'Salvestatud · v{n}',
    unshare: 'Lõpeta jagamine',
    noLongerShared: 'Seda rakendust enam ei jagata. Palu kasutajal {who} seda uuesti jagada.',
    back: 'Raamatukogu',
    tabsLabel: 'Rakenduse jaotised',
    tabApp: 'Rakendus',
    tabData: 'Andmed',
    tabHistory: 'Ajalugu',
    tabVersions: 'Versioonid',
    owner: 'Omanik: {who}',
    moreActions: 'Rohkem toiminguid',
    duplicate: 'Tee koopia…',
    reset: 'Tühjenda kõik kirjed…',
    openChat: 'Ava algne vestlus',
    changeInChat: 'Muuda vestluses',
    changeInChatUnavailable: 'Vestlus, milles see rakendus loodi, pole saadaval.',
    failTitle: 'Versioon {n} lakkas kell {time} laadimast. See puudutab {people} inimest.',
    failGoBack: 'Mine kõigi jaoks tagasi versioonile v{n}',
    failFix: 'Paranda vestluses',
    failDetails: 'Üksikasjad',
    failEmpty:
      'See versioon ei laadinud. Inimesed näevad sama teadet ja võimalust sulle teada anda.',
    failUser: 'See rakendus ei laadinud. Teavita kasutajat {who}, et ta saaks selle parandada.',
    failTell: 'Teavita kasutajat {who}',
    deletedTitle: 'See rakendus on kustutatud',
    deletedBody:
      '{who} kustutas rakenduse {app} kuupäeval {date}. Selle kirjeid säilitatakse {days} päeva juhuks, kui see taastatakse.',
    toLibrary: 'Ava Raamatukogu',
    askPerson: 'Küsi kasutajalt {who}',
    loading: 'Rakenduse {app} avamine…',
    appUnavailable: 'Seda rakendust ei saa praegu avada.',
    portionUnavailable: 'See osa pole praegu saadaval.',
    appNoBuiltVersion: 'Sellel rakendusel pole veel loodud versiooni, mida avada.',
  },
  fi: {
    saved: 'Tallennettu · v{n}',
    unshare: 'Lopeta jakaminen',
    noLongerShared: 'Tätä sovellusta ei enää jaeta. Pyydä henkilöä {who} jakamaan se uudelleen.',
    back: 'Kirjasto',
    tabsLabel: 'Sovelluksen osiot',
    tabApp: 'Sovellus',
    tabData: 'Tiedot',
    tabHistory: 'Historia',
    tabVersions: 'Versiot',
    owner: 'Omistaja: {who}',
    moreActions: 'Lisää toimintoja',
    duplicate: 'Monista…',
    reset: 'Tyhjennä kaikki merkinnät…',
    openChat: 'Avaa alkuperäinen chat',
    changeInChat: 'Muuta chatissa',
    changeInChatUnavailable: 'Chatti, jossa tämä sovellus tehtiin, ei ole käytettävissä.',
    failTitle: 'Versio {n} lakkasi latautumasta klo {time}. Se koskee {people} henkilöä.',
    failGoBack: 'Palaa versioon v{n} kaikille',
    failFix: 'Korjaa chatissa',
    failDetails: 'Lisätiedot',
    failEmpty:
      'Tämä versio ei latautunut. Käyttäjät näkevät saman viestin ja tavan ilmoittaa sinulle.',
    failUser:
      'Tämä sovellus ei latautunut. Kerro asiasta käyttäjälle {who}, jotta hän voi korjata sen.',
    failTell: 'Kerro käyttäjälle {who}',
    deletedTitle: 'Tämä sovellus poistettiin',
    deletedBody:
      '{who} poisti sovelluksen {app} {date}. Sen merkinnät säilytetään {days} päivää siltä varalta, että se palautetaan.',
    toLibrary: 'Siirry Kirjastoon',
    askPerson: 'Kysy käyttäjältä {who}',
    loading: 'Avataan sovellusta {app}…',
    appUnavailable: 'Tätä sovellusta ei voi avata juuri nyt.',
    portionUnavailable: 'Tämä osa ei ole juuri nyt käytettävissä.',
    appNoBuiltVersion: 'Tällä sovelluksella ei ole vielä luotua versiota avattavaksi.',
  },
  fr: {
    saved: 'Enregistré · v{n}',
    unshare: 'Ne plus partager',
    noLongerShared: 'Cette App n’est plus partagée. Demandez à {who} de la partager à nouveau.',
    back: 'Bibliothèque',
    tabsLabel: 'Sections de l’app',
    tabApp: 'App',
    tabData: 'Données',
    tabHistory: 'Historique',
    tabVersions: 'Versions',
    owner: 'Propriétaire : {who}',
    moreActions: 'Plus d’actions',
    duplicate: 'Dupliquer…',
    reset: 'Effacer toutes les entrées…',
    openChat: 'Ouvrir le chat d’origine',
    changeInChat: 'Modifier dans le chat',
    changeInChatUnavailable: 'Le chat dans lequel cette app a été créée n’est pas disponible.',
    failTitle: 'La version {n} ne se charge plus pour {people} personnes depuis {time}.',
    failGoBack: 'Revenir à v{n} pour tous',
    failFix: 'Corriger dans le chat',
    failDetails: 'Détails',
    failEmpty:
      'Cette version ne s’est pas chargée. Les utilisateurs voient le même message et un moyen de vous prévenir.',
    failUser: 'Cette app ne s’est pas chargée. Prévenez {who} pour que le problème soit corrigé.',
    failTell: 'Prévenir {who}',
    deletedTitle: 'Cette app a été supprimée',
    deletedBody:
      '{who} a supprimé {app} le {date}. Ses entrées sont conservées {days} jours au cas où elle serait restaurée.',
    toLibrary: 'Aller à la bibliothèque',
    askPerson: 'Demander à {who}',
    loading: 'Ouverture de {app}…',
    appUnavailable: 'Cette app ne peut pas être ouverte pour le moment.',
    portionUnavailable: 'Cette partie n’est pas disponible pour le moment.',
    appNoBuiltVersion: 'Cette app n’a pas encore de version créée à ouvrir.',
  },
  ga: {
    saved: 'Sábháilte · v{n}',
    unshare: 'Stop ag comhroinnt',
    noLongerShared: 'Níl an App seo á comhroinnt a thuilleadh. Iarr ar {who} í a chomhroinnt arís.',
    back: 'Leabharlann',
    tabsLabel: 'Codanna den aip',
    tabApp: 'Aip',
    tabData: 'Sonraí',
    tabHistory: 'Stair',
    tabVersions: 'Leaganacha',
    owner: 'Úinéir: {who}',
    moreActions: 'Tuilleadh gníomhartha',
    duplicate: 'Dúblaigh…',
    reset: 'Glan na hiontrálacha go léir…',
    openChat: 'Oscail an comhrá bunaidh',
    changeInChat: 'Athraigh sa chomhrá',
    changeInChatUnavailable: 'Níl an comhrá inar rinneadh an aip seo ar fáil.',
    failTitle: 'Stop leagan {n} ag lódáil ag {time}. Daoine a raibh tionchar air orthu: {people}.',
    failGoBack: 'Téigh ar ais go v{n} do chách',
    failFix: 'Ceartaigh sa chomhrá',
    failDetails: 'Mionsonraí',
    failEmpty:
      'Níor lódáil an leagan seo. Feiceann daoine an teachtaireacht chéanna agus bealach le hinsint duit.',
    failUser: 'Níor lódáil an aip seo. Inis do {who} ionas gur féidir an fhadhb a cheartú.',
    failTell: 'Inis do {who}',
    deletedTitle: 'Scriosadh an aip seo',
    deletedBody:
      'Scrios {who} {app} ar {date}. Coinnítear a cuid iontrálacha le linn na tréimhse coinneála (laethanta: {days}) ar eagla go n-athchóireofar í.',
    toLibrary: 'Téigh go dtí an Leabharlann',
    askPerson: 'Fiafraigh de {who}',
    loading: '{app} á oscailt…',
    appUnavailable: 'Ní féidir an aip seo a oscailt faoi láthair.',
    portionUnavailable: 'Níl an chuid seo ar fáil faoi láthair.',
    appNoBuiltVersion: 'Níl leagan cruthaithe ag an aip seo le hoscailt fós.',
  },
  hr: {
    saved: 'Spremljeno · v{n}',
    unshare: 'Prestani dijeliti',
    noLongerShared: 'Ova se aplikacija više ne dijeli. Zamolite {who} da je ponovno podijeli.',
    back: 'Knjižnica',
    tabsLabel: 'Odjeljci aplikacije',
    tabApp: 'Aplikacija',
    tabData: 'Podaci',
    tabHistory: 'Povijest',
    tabVersions: 'Verzije',
    owner: 'Vlasnik: {who}',
    moreActions: 'Više radnji',
    duplicate: 'Dupliciraj…',
    reset: 'Izbriši sve unose…',
    openChat: 'Otvori izvorni chat',
    changeInChat: 'Promijeni u chatu',
    changeInChatUnavailable: 'Chat u kojem je ova aplikacija nastala nije dostupan.',
    failTitle: 'Verzija {n} prestala se učitavati u {time}. Broj pogođenih osoba: {people}.',
    failGoBack: 'Vrati v{n} za sve',
    failFix: 'Popravi u chatu',
    failDetails: 'Pojedinosti',
    failEmpty: 'Ova se verzija nije učitala. Korisnici vide istu poruku i način da vas obavijeste.',
    failUser:
      'Ova se aplikacija nije učitala. Obavijestite osobu {who} kako bi je mogla popraviti.',
    failTell: 'Obavijesti osobu {who}',
    deletedTitle: 'Ova je aplikacija izbrisana',
    deletedBody:
      'Osoba {who} izbrisala je aplikaciju {app} {date}. Unosi se za slučaj vraćanja čuvaju u ograničenom roku (broj dana: {days}).',
    toLibrary: 'Idi u knjižnicu',
    askPerson: 'Pitaj osobu {who}',
    loading: 'Otvaranje aplikacije {app}…',
    appUnavailable: 'Ova se aplikacija trenutačno ne može otvoriti.',
    portionUnavailable: 'Ovaj dio trenutačno nije dostupan.',
    appNoBuiltVersion: 'Ova aplikacija još nema izrađenu verziju koju bi se moglo otvoriti.',
  },
  hu: {
    saved: 'Mentve · v{n}',
    unshare: 'Megosztás megszüntetése',
    noLongerShared:
      'Ez az alkalmazás már nincs megosztva. Kérje meg {who} felhasználót, hogy ossza meg újra.',
    back: 'Könyvtár',
    tabsLabel: 'Az alkalmazás részei',
    tabApp: 'Alkalmazás',
    tabData: 'Adatok',
    tabHistory: 'Előzmények',
    tabVersions: 'Verziók',
    owner: 'Tulajdonos: {who}',
    moreActions: 'További műveletek',
    duplicate: 'Másolat készítése…',
    reset: 'Összes bejegyzés törlése…',
    openChat: 'Eredeti csevegés megnyitása',
    changeInChat: 'Módosítás a csevegésben',
    changeInChatUnavailable: 'Az a csevegés, amelyben ez az alkalmazás készült, nem érhető el.',
    failTitle: 'A(z) {n}. verzió {time} óta nem töltődik be {people} felhasználónál.',
    failGoBack: 'Visszatérés a v{n} verzióhoz mindenkinél',
    failFix: 'Javítás a csevegésben',
    failDetails: 'Részletek',
    failEmpty:
      'Ez a verzió nem töltődött be. A felhasználók ugyanezt az üzenetet látják, és jelezhetik Önnek.',
    failUser: 'Ez az alkalmazás nem töltődött be. Jelezze a hibát neki: {who}, hogy kijavíthassa.',
    failTell: 'Jelzés neki: {who}',
    deletedTitle: 'Ezt az alkalmazást törölték',
    deletedBody:
      '{who} törölte a(z) {app} alkalmazást ({date}). A bejegyzéseit {days} napig megőrizzük arra az esetre, ha visszaállítanák.',
    toLibrary: 'Ugrás a Könyvtárba',
    askPerson: 'Kérdezze meg: {who}',
    loading: 'A(z) {app} megnyitása…',
    appUnavailable: 'Ezt az alkalmazást most nem lehet megnyitni.',
    portionUnavailable: 'Ez a rész most nem érhető el.',
    appNoBuiltVersion: 'Ennek az alkalmazásnak még nincs megnyitható elkészült verziója.',
  },
  it: {
    saved: 'Salvato · v{n}',
    unshare: 'Interrompi condivisione',
    noLongerShared: 'Questa App non è più condivisa. Chiedi a {who} di condividerla di nuovo.',
    back: 'Libreria',
    tabsLabel: 'Sezioni dell’app',
    tabApp: 'App',
    tabData: 'Dati',
    tabHistory: 'Cronologia',
    tabVersions: 'Versioni',
    owner: 'Proprietario: {who}',
    moreActions: 'Altre azioni',
    duplicate: 'Duplica…',
    reset: 'Cancella tutte le voci…',
    openChat: 'Apri la chat originale',
    changeInChat: 'Modifica in chat',
    changeInChatUnavailable: 'La chat in cui è stata creata questa App non è disponibile.',
    failTitle: 'Alle {time} la versione {n} ha smesso di caricarsi per {people} persone.',
    failGoBack: 'Torna a v{n} per tutti',
    failFix: 'Correggi in chat',
    failDetails: 'Dettagli',
    failEmpty:
      'Questa versione non si è caricata. Le persone vedono lo stesso messaggio e un modo per avvisarti.',
    failUser: 'Questa app non si è caricata. Avvisa {who} perché possa correggerla.',
    failTell: 'Avvisa {who}',
    deletedTitle: 'Questa app è stata eliminata',
    deletedBody:
      '{who} ha eliminato {app} il {date}. Le sue voci vengono conservate per {days} giorni nel caso venga ripristinata.',
    toLibrary: 'Vai alla Libreria',
    askPerson: 'Chiedi a {who}',
    loading: 'Apertura di {app}…',
    appUnavailable: 'Questa app non può essere aperta in questo momento.',
    portionUnavailable: 'Questa parte non è disponibile in questo momento.',
    appNoBuiltVersion: 'Questa App non ha ancora una versione creata da aprire.',
  },
  lt: {
    saved: 'Išsaugota · v{n}',
    unshare: 'Nebebendrinti',
    noLongerShared: 'Ši programėlė nebebendrinama. Paprašykite {who} ją bendrinti dar kartą.',
    back: 'Biblioteka',
    tabsLabel: 'Programėlės skiltys',
    tabApp: 'Programėlė',
    tabData: 'Duomenys',
    tabHistory: 'Istorija',
    tabVersions: 'Versijos',
    owner: 'Savininkas: {who}',
    moreActions: 'Daugiau veiksmų',
    duplicate: 'Dubliuoti…',
    reset: 'Išvalyti visus įrašus…',
    openChat: 'Atidaryti pradinį pokalbį',
    changeInChat: 'Keisti pokalbyje',
    changeInChatUnavailable: 'Pokalbis, kuriame sukurta ši programėlė, nepasiekiamas.',
    failTitle: 'Versija {n} nustojo įsikelti {time}. Paveiktų žmonių: {people}.',
    failGoBack: 'Grąžinti v{n} visiems',
    failFix: 'Taisyti pokalbyje',
    failDetails: 'Išsami informacija',
    failEmpty: 'Ši versija neįsikėlė. Žmonės mato tą patį pranešimą ir būdą jums pranešti.',
    failUser: 'Ši programėlė neįsikėlė. Praneškite naudotojui {who}, kad galėtų ją pataisyti.',
    failTell: 'Pranešti: {who}',
    deletedTitle: 'Ši programėlė ištrinta',
    deletedBody:
      '{who} ištrynė {app} {date}. Jos įrašai saugomi {days} d., jei ją reikėtų atkurti.',
    toLibrary: 'Eiti į biblioteką',
    askPerson: 'Klausti: {who}',
    loading: 'Atidaroma {app}…',
    appUnavailable: 'Šios programos dabar atidaryti negalima.',
    portionUnavailable: 'Ši dalis dabar nepasiekiama.',
    appNoBuiltVersion: 'Ši programėlė dar neturi sukurtos versijos, kurią būtų galima atidaryti.',
  },
  lv: {
    saved: 'Saglabāts · v{n}',
    unshare: 'Pārtraukt kopīgošanu',
    noLongerShared: 'Šī lietotne vairs netiek kopīgota. Lūdziet {who} to kopīgot vēlreiz.',
    back: 'Bibliotēka',
    tabsLabel: 'Lietotnes sadaļas',
    tabApp: 'Lietotne',
    tabData: 'Dati',
    tabHistory: 'Vēsture',
    tabVersions: 'Versijas',
    owner: 'Īpašnieks: {who}',
    moreActions: 'Citas darbības',
    duplicate: 'Dublēt…',
    reset: 'Notīrīt visus ierakstus…',
    openChat: 'Atvērt sākotnējo tērzēšanu',
    changeInChat: 'Mainīt tērzēšanā',
    changeInChatUnavailable: 'Tērzēšana, kurā izveidota šī lietotne, nav pieejama.',
    failTitle: 'Versija {n} pārstāja ielādēties plkst. {time}. Skartie cilvēki: {people}.',
    failGoBack: 'Atgriezties pie v{n} visiem',
    failFix: 'Labot tērzēšanā',
    failDetails: 'Sīkāk',
    failEmpty:
      'Šī versija netika ielādēta. Cilvēki redz to pašu ziņojumu un iespēju jums par to paziņot.',
    failUser: 'Šī lietotne netika ielādēta. Informējiet: {who}, lai to varētu salabot.',
    failTell: 'Informēt: {who}',
    deletedTitle: 'Šī lietotne tika izdzēsta',
    deletedBody:
      '{who} izdzēsa {app} {date}. Tās ieraksti tiek glabāti {days} d. gadījumam, ja to atjaunos.',
    toLibrary: 'Uz bibliotēku',
    askPerson: 'Jautāt: {who}',
    loading: 'Atver {app}…',
    appUnavailable: 'Šo lietotni pašlaik nevar atvērt.',
    portionUnavailable: 'Šī daļa pašlaik nav pieejama.',
    appNoBuiltVersion: 'Šai lietotnei vēl nav izveidotas versijas, ko atvērt.',
  },
  mt: {
    saved: 'Issejvjata · v{n}',
    unshare: 'Waqqaf il-qsim',
    noLongerShared: 'Din l-App m’għadhiex maqsuma. Itlob lil {who} jaqsamha mill-ġdid.',
    back: 'Librerija',
    tabsLabel: 'Taqsimiet tal-app',
    tabApp: 'App',
    tabData: 'Data',
    tabHistory: 'Storja',
    tabVersions: 'Verżjonijiet',
    owner: 'Sid: {who}',
    moreActions: 'Aktar azzjonijiet',
    duplicate: 'Iddoppja…',
    reset: 'Naddaf l-entrati kollha…',
    openChat: 'Iftaħ iċ-chat oriġinali',
    changeInChat: 'Ibdel fiċ-chat',
    changeInChatUnavailable: 'Iċ-chat li fih saret din l-app mhux disponibbli.',
    failTitle: 'Il-verżjoni {n} waqfet titgħabba fil-{time}. Nies affettwati: {people}.',
    failGoBack: 'Mur lura għal v{n} għal kulħadd',
    failFix: 'Irranġa fiċ-chat',
    failDetails: 'Dettalji',
    failEmpty: 'Din il-verżjoni ma tgħabbietx. In-nies jaraw l-istess messaġġ u mod kif jgħarrfuk.',
    failUser: 'Din l-app ma tgħabbietx. Għid lil {who} biex tkun tista’ tiġi rranġata.',
    failTell: 'Għid lil {who}',
    deletedTitle: 'Din l-app tħassret',
    deletedBody:
      '{who} ħassar {app} fil-{date}. L-entrati tagħha jinżammu matul il-perjodu taż-żamma (jiem: {days}) f’każ li terġa’ tiġi rrestawrata.',
    toLibrary: 'Mur il-Librerija',
    askPerson: 'Staqsi lil {who}',
    loading: 'Qed tinfetaħ {app}…',
    appUnavailable: 'Din l-app ma tistax tinfetaħ bħalissa.',
    portionUnavailable: 'Din il-parti mhix disponibbli bħalissa.',
    appNoBuiltVersion: 'Din l-app għad m’għandhiex verżjoni maħluqa biex tinfetaħ.',
  },
  nl: {
    saved: 'Opgeslagen · v{n}',
    unshare: 'Niet meer delen',
    noLongerShared: 'Deze App wordt niet meer gedeeld. Vraag {who} om deze opnieuw te delen.',
    back: 'Bibliotheek',
    tabsLabel: 'Onderdelen van de app',
    tabApp: 'App',
    tabData: 'Gegevens',
    tabHistory: 'Geschiedenis',
    tabVersions: 'Versies',
    owner: 'Eigenaar: {who}',
    moreActions: 'Meer acties',
    duplicate: 'Dupliceren…',
    reset: 'Alle items wissen…',
    openChat: 'Oorspronkelijke chat openen',
    changeInChat: 'Wijzigen in chat',
    changeInChatUnavailable: 'De chat waarin deze app is gemaakt, is niet beschikbaar.',
    failTitle: 'Versie {n} laadt sinds {time} niet meer voor {people} personen.',
    failGoBack: 'Voor iedereen terug naar v{n}',
    failFix: 'Oplossen in chat',
    failDetails: 'Meer info',
    failEmpty:
      'Deze versie is niet geladen. Mensen zien hetzelfde bericht en een manier om het jou te laten weten.',
    failUser: 'Deze app is niet geladen. Laat het {who} weten, zodat het opgelost kan worden.',
    failTell: '{who} inlichten',
    deletedTitle: 'Deze app is verwijderd',
    deletedBody:
      '{who} heeft {app} op {date} verwijderd. De items worden {days} dagen bewaard voor het geval de app wordt hersteld.',
    toLibrary: 'Naar de bibliotheek',
    askPerson: '{who} vragen',
    loading: '{app} openen…',
    appUnavailable: 'Deze app kan nu niet worden geopend.',
    portionUnavailable: 'Dit onderdeel is nu niet beschikbaar.',
    appNoBuiltVersion: 'Deze app heeft nog geen gemaakte versie om te openen.',
  },
  pl: {
    saved: 'Zapisano · v{n}',
    unshare: 'Przestań udostępniać',
    noLongerShared: 'Ta aplikacja nie jest już udostępniana. Poproś {who} o ponowne udostępnienie.',
    back: 'Biblioteka',
    tabsLabel: 'Sekcje aplikacji',
    tabApp: 'Aplikacja',
    tabData: 'Dane',
    tabHistory: 'Historia',
    tabVersions: 'Wersje',
    owner: 'Właściciel: {who}',
    moreActions: 'Więcej działań',
    duplicate: 'Duplikuj…',
    reset: 'Wyczyść wszystkie wpisy…',
    openChat: 'Otwórz oryginalny czat',
    changeInChat: 'Zmień na czacie',
    changeInChatUnavailable: 'Czat, w którym powstała ta aplikacja, jest niedostępny.',
    failTitle:
      'Wersja {n} przestała się wczytywać o {time}. Liczba osób, których to dotyczy: {people}.',
    failGoBack: 'Wróć do v{n} dla wszystkich',
    failFix: 'Napraw na czacie',
    failDetails: 'Szczegóły',
    failEmpty:
      'Ta wersja się nie wczytała. Użytkownicy widzą ten sam komunikat i sposób, by dać Ci znać.',
    failUser: 'Ta aplikacja się nie wczytała. Daj znać tej osobie: {who}, aby mogła ją naprawić.',
    failTell: 'Powiadom: {who}',
    deletedTitle: 'Ta aplikacja została usunięta',
    deletedBody:
      'Usunięto aplikację {app} ({who}, {date}). Wpisy są przechowywane na wypadek przywrócenia przez ograniczony czas (liczba dni: {days}).',
    toLibrary: 'Przejdź do Biblioteki',
    askPerson: 'Zapytaj: {who}',
    loading: 'Otwieranie {app}…',
    appUnavailable: 'Tej aplikacji nie można teraz otworzyć.',
    portionUnavailable: 'Ta część jest teraz niedostępna.',
    appNoBuiltVersion: 'Ta aplikacja nie ma jeszcze utworzonej wersji do otwarcia.',
  },
  pt: {
    saved: 'Guardado · v{n}',
    unshare: 'Deixar de partilhar',
    noLongerShared: 'Esta App já não é partilhada. Peça a {who} para a partilhar novamente.',
    back: 'Biblioteca',
    tabsLabel: 'Secções da app',
    tabApp: 'App',
    tabData: 'Dados',
    tabHistory: 'Histórico',
    tabVersions: 'Versões',
    owner: 'Proprietário: {who}',
    moreActions: 'Mais ações',
    duplicate: 'Duplicar…',
    reset: 'Limpar todos os registos…',
    openChat: 'Abrir o chat original',
    changeInChat: 'Alterar no chat',
    changeInChatUnavailable: 'O chat em que esta App foi criada não está disponível.',
    failTitle: 'A versão {n} deixou de carregar para {people} pessoas às {time}.',
    failGoBack: 'Voltar à v{n} para todos',
    failFix: 'Corrigir no chat',
    failDetails: 'Detalhes',
    failEmpty:
      'Esta versão não carregou. As pessoas veem a mesma mensagem e uma forma de o avisar.',
    failUser: 'Esta app não carregou. Avise {who} para que o problema possa ser corrigido.',
    failTell: 'Avisar {who}',
    deletedTitle: 'Esta app foi eliminada',
    deletedBody:
      '{who} eliminou {app} a {date}. Os registos são guardados durante {days} dias caso a app seja restaurada.',
    toLibrary: 'Ir para a Biblioteca',
    askPerson: 'Perguntar a {who}',
    loading: 'A abrir {app}…',
    appUnavailable: 'Esta app não pode ser aberta neste momento.',
    portionUnavailable: 'Esta parte não está disponível neste momento.',
    appNoBuiltVersion: 'Esta App ainda não tem uma versão criada para abrir.',
  },
  ro: {
    saved: 'Salvat · v{n}',
    unshare: 'Oprește partajarea',
    noLongerShared:
      'Această aplicație nu mai este partajată. Cereți-i lui {who} să o partajeze din nou.',
    back: 'Bibliotecă',
    tabsLabel: 'Secțiunile aplicației',
    tabApp: 'Aplicație',
    tabData: 'Date',
    tabHistory: 'Istoric',
    tabVersions: 'Versiuni',
    owner: 'Proprietar: {who}',
    moreActions: 'Mai multe acțiuni',
    duplicate: 'Duplicați…',
    reset: 'Goliți toate înregistrările…',
    openChat: 'Deschideți chatul inițial',
    changeInChat: 'Modificați în chat',
    changeInChatUnavailable: 'Chatul în care a fost creată această aplicație nu este disponibil.',
    failTitle: 'Versiunea {n} nu se mai încarcă de la ora {time}. Persoane afectate: {people}.',
    failGoBack: 'Reveniți la v{n} pentru toți',
    failFix: 'Remediați în chat',
    failDetails: 'Detalii',
    failEmpty:
      'Această versiune nu s-a încărcat. Utilizatorii văd același mesaj și o modalitate de a vă anunța.',
    failUser:
      'Această aplicație nu s-a încărcat. Anunțați persoana {who}, ca problema să poată fi remediată.',
    failTell: 'Anunțați persoana {who}',
    deletedTitle: 'Această aplicație a fost ștearsă',
    deletedBody:
      '{who} a șters {app} pe {date}. Înregistrările sunt păstrate în perioada de păstrare (zile: {days}), în caz că aplicația este restaurată.',
    toLibrary: 'Accesați Biblioteca',
    askPerson: 'Întrebați persoana {who}',
    loading: 'Se deschide {app}…',
    appUnavailable: 'Această aplicație nu poate fi deschisă acum.',
    portionUnavailable: 'Această parte nu este disponibilă acum.',
    appNoBuiltVersion: 'Această aplicație nu are încă o versiune creată care să poată fi deschisă.',
  },
  sk: {
    saved: 'Uložené · v{n}',
    unshare: 'Prestať zdieľať',
    noLongerShared: 'Táto aplikácia sa už nezdieľa. Požiadajte {who} o opätovné zdieľanie.',
    back: 'Knižnica',
    tabsLabel: 'Časti aplikácie',
    tabApp: 'Aplikácia',
    tabData: 'Údaje',
    tabHistory: 'História',
    tabVersions: 'Verzie',
    owner: 'Vlastník: {who}',
    moreActions: 'Ďalšie akcie',
    duplicate: 'Duplikovať…',
    reset: 'Vymazať všetky záznamy…',
    openChat: 'Otvoriť pôvodný chat',
    changeInChat: 'Zmeniť v chate',
    changeInChatUnavailable: 'Chat, v ktorom táto aplikácia vznikla, nie je dostupný.',
    failTitle: 'Verzia {n} sa prestala načítavať o {time}. Počet dotknutých ľudí: {people}.',
    failGoBack: 'Vrátiť sa k v{n} pre všetkých',
    failFix: 'Opraviť v chate',
    failDetails: 'Podrobnosti',
    failEmpty: 'Táto verzia sa nenačítala. Ľudia vidia rovnakú správu a možnosť dať vám vedieť.',
    failUser: 'Táto aplikácia sa nenačítala. Dajte vedieť osobe {who}, aby ju mohla opraviť.',
    failTell: 'Dať vedieť osobe {who}',
    deletedTitle: 'Táto aplikácia bola odstránená',
    deletedBody:
      'Aplikáciu {app} odstránila dňa {date} osoba {who}. Záznamy sa pre prípad obnovenia uchovávajú počas obmedzenej doby (počet dní: {days}).',
    toLibrary: 'Prejsť do Knižnice',
    askPerson: 'Opýtať sa osoby {who}',
    loading: 'Otvára sa {app}…',
    appUnavailable: 'Túto aplikáciu teraz nemožno otvoriť.',
    portionUnavailable: 'Táto časť teraz nie je k dispozícii.',
    appNoBuiltVersion: 'Táto aplikácia zatiaľ nemá vytvorenú verziu, ktorú by bolo možné otvoriť.',
  },
  sl: {
    saved: 'Shranjeno · v{n}',
    unshare: 'Nehaj deliti',
    noLongerShared: 'Ta aplikacija ni več v skupni rabi. Prosite {who}, naj jo znova deli.',
    back: 'Knjižnica',
    tabsLabel: 'Razdelki aplikacije',
    tabApp: 'Aplikacija',
    tabData: 'Podatki',
    tabHistory: 'Zgodovina',
    tabVersions: 'Različice',
    owner: 'Lastnik: {who}',
    moreActions: 'Več dejanj',
    duplicate: 'Podvoji…',
    reset: 'Izbriši vse vnose…',
    openChat: 'Odpri izvirni klepet',
    changeInChat: 'Spremeni v klepetu',
    changeInChatUnavailable: 'Klepet, v katerem je nastala ta aplikacija, ni na voljo.',
    failTitle:
      'Različica {n} se je ob {time} prenehala nalagati. Število prizadetih oseb: {people}.',
    failGoBack: 'Vrni v{n} za vse',
    failFix: 'Popravi v klepetu',
    failDetails: 'Podrobnosti',
    failEmpty:
      'Ta različica se ni naložila. Uporabniki vidijo isto sporočilo in način, da vas obvestijo.',
    failUser: 'Ta aplikacija se ni naložila. Obvestite osebo {who}, da jo lahko popravi.',
    failTell: 'Obvesti osebo {who}',
    deletedTitle: 'Ta aplikacija je bila izbrisana',
    deletedBody:
      'Oseba {who} je {date} izbrisala aplikacijo {app}. Vnosi se za primer obnovitve hranijo v omejenem času (število dni: {days}).',
    toLibrary: 'Pojdi v knjižnico',
    askPerson: 'Vprašaj osebo {who}',
    loading: 'Odpiranje aplikacije {app}…',
    appUnavailable: 'Te aplikacije trenutno ni mogoče odpreti.',
    portionUnavailable: 'Ta del trenutno ni na voljo.',
    appNoBuiltVersion: 'Ta aplikacija še nima ustvarjene različice, ki bi jo bilo mogoče odpreti.',
  },
};

export const appPageCopy: NamespaceCatalog<AppPageCopyKey> = {
  en,
  bg: translated.bg,
  cs: translated.cs,
  da: translated.da,
  de: translated.de,
  el: translated.el,
  es: translated.es,
  et: translated.et,
  fi: translated.fi,
  fr: translated.fr,
  ga: translated.ga,
  hr: translated.hr,
  hu: translated.hu,
  it: translated.it,
  lt: translated.lt,
  lv: translated.lv,
  mt: translated.mt,
  nl: translated.nl,
  pl: translated.pl,
  pt: translated.pt,
  ro: translated.ro,
  sk: translated.sk,
  sl: translated.sl,
  sv,
};
export default appPageCopy;
